import ExpandMore from '@ixigo/icons/ExpandMore';
import Accordion from '@ixigo/iui/Accordion';
import AccordionBody from '@ixigo/iui/AccordionBody';
import AccordionHead from '@ixigo/iui/AccordionHead';
import Button from '@ixigo/iui/Button';
import Collapse from '@ixigo/iui/Collapse';
import Typography from '@ixigo/iui/Typography';
import { useMemo, useState } from 'react';
import type { FaqResponse, QnA } from '@/routes/-home/-api';

export function generateFAQScriptTag(questionsAndAnswers: QnA[]): string {
	const faqPageSchema = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: questionsAndAnswers.map((qa) => ({
			'@type': 'Question',
			name: qa.q,
			acceptedAnswer: {
				'@type': 'Answer',
				text: qa.a,
			},
		})),
	};

	const scriptTag = `<script type='application/ld+json'>${JSON.stringify(
		faqPageSchema,
	)}</script>`;

	return scriptTag;
}

const FaqInfo = ({
	qnas,
	defaultExpanded = false,
	className = '',
}: {
	qnas: QnA;
	defaultExpanded?: boolean;
	className?: string;
}) => {
	const { q, a } = qnas;
	return (
		<Accordion
			defaultExpanded={defaultExpanded}
			className={`pt-10 ${className}`}
		>
			<AccordionHead expandIcon={<ExpandMore />} className="!items-start">
				<h3>{q}</h3>
			</AccordionHead>
			<AccordionBody>
				<div
					className="text-secondary"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: <need to add schema for faq>
					dangerouslySetInnerHTML={{ __html: a }}
				/>
			</AccordionBody>
		</Accordion>
	);
};

export const FaqSection = ({ faqData }: { faqData: FaqResponse }) => {
	const [isViewAll, setViewAll] = useState(false);

	const {
		title = '',
		qnas = [],
		noExpandedFaqs = 1,
		noVisibleFaqs = 6,
	} = faqData;
	const faqPageScriptTag = generateFAQScriptTag(qnas);
	const onViewAllClick = () => {
		setViewAll((isViewAll) => !isViewAll);
	};
	const initialVisible = useMemo(() => {
		return qnas.slice(0, noVisibleFaqs);
	}, [qnas, noVisibleFaqs]);
	const initialHidden = useMemo(() => {
		return qnas.slice(noVisibleFaqs, qnas.length);
	}, [qnas, noVisibleFaqs]);

	if (!qnas.length) return null;

	return (
		<section className="bg-primary p-20">
			<div className="px-20 py-15 border rounded-20">
				<Typography fontWeight="medium" className="lg:font-bold" variant="h5">
					{title}
				</Typography>
				{/** biome-ignore lint/security/noDangerouslySetInnerHtml: <need to add schema for faq> */}
				<div dangerouslySetInnerHTML={{ __html: faqPageScriptTag }} />
				{initialVisible?.map((item, index) => {
					const count = index + 1;
					return (
						<FaqInfo
							key={index}
							qnas={item}
							defaultExpanded={count <= noExpandedFaqs && true}
						/>
					);
				})}
				<Collapse in={isViewAll}>
					{initialHidden?.map((item, index) => {
						const count = initialVisible.length + index + 1;
						return (
							<FaqInfo
								key={index}
								qnas={item}
								defaultExpanded={count <= noExpandedFaqs && true}
							/>
						);
					})}
				</Collapse>

				{initialHidden.length > 0 && (
					<div className="flex justify-center mt-2">
						<Button
							variant="flat"
							size="md"
							onClick={onViewAllClick}
							className="w-full"
						>
							{isViewAll ? 'View Less' : 'View More'}
						</Button>
					</div>
				)}
			</div>
		</section>
	);
};
